import React from 'react';

import { LabelInput } from '@/components/ui/inputs';
import { SidePanelFormLayout } from '@/components/layout';
import { Text } from '@/components/ui/text/Text';
import { colors } from '@/assets/styles/colors';
import { useTranslation } from '@/i18n';
import { formatToDdMmYyyyHHmmss } from '@/utils';
import type { Device } from '@/api/domains/device';


interface DeviceDetailPanelProps {
  device: Device;
}

export const DeviceDetailPanel: React.FC<DeviceDetailPanelProps> = ({ device }) => {
  const { t: translate } = useTranslation();

  return (
    <SidePanelFormLayout>
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('device:detail.fields.deviceId')}
          value={String(device.deviceId)}
          disabled
          fullWidth
          data-testid="device-detail-deviceId"
        />
        <LabelInput
          label={translate('device:detail.fields.deviceType')}
          value={device.deviceType}
          disabled
          fullWidth
          data-testid="device-detail-deviceType"
        />
        <LabelInput
          label={translate('device:detail.fields.name')}
          value={device.name}
          disabled
          fullWidth
          data-testid="device-detail-name"
        />
        <LabelInput
          label={translate('device:detail.fields.code')}
          value={device.code}
          disabled
          fullWidth
          data-testid="device-detail-code"
        />
        <LabelInput
          label={translate('device:detail.fields.serialNumber')}
          value={device.serialNumber || '-'}
          disabled
          fullWidth
          data-testid="device-detail-serialNumber"
        />
        <LabelInput
          label={translate('device:detail.fields.locationName')}
          value={device.locationName || '-'}
          disabled
          fullWidth
          data-testid="device-detail-locationName"
        />
        <LabelInput
          label={translate('device:detail.fields.deviceGroupName')}
          value={device.deviceGroupName || '-'}
          disabled
          fullWidth
          data-testid="device-detail-deviceGroupName"
        />
        <LabelInput
          label={translate('device:detail.fields.registeredAt')}
          value={formatToDdMmYyyyHHmmss(device.registeredAt) || '-'}
          disabled
          fullWidth
          data-testid="device-detail-registeredAt"
        />
        <LabelInput
          label={translate('device:detail.fields.lastConnectionAt')}
          value={formatToDdMmYyyyHHmmss(device.lastConnectionAt) || '-'}
          disabled
          fullWidth
          data-testid="device-detail-lastConnectionAt"
        />
        <div>
          <Text variant="body-regular" color={colors.grays.neutral66}>
            {translate('device:detail.fields.status')}
          </Text>
          <Text
            variant="subheader-medium"
            color={device.isConnected ? colors.feedback.success400 : colors.feedback.error400}
            as="span"
            data-testid="device-detail-status"
          >
            {device.isConnected ? translate('device:list.status.connected') : translate('device:list.status.disconnected')}
          </Text>
        </div>
        <LabelInput
          label={translate('device:detail.fields.userCount')}
          value={String(device.userCount ?? 0)}
          disabled
          fullWidth
          data-testid="device-detail-userCount"
        />
        <LabelInput
          label={translate('device:detail.fields.fingerprintCount')}
          value={String(device.fingerprintCount ?? 0)}
          disabled
          fullWidth
          data-testid="device-detail-fingerprintCount"
        />
        <LabelInput
          label={translate('device:detail.fields.faceCount')}
          value={String(device.faceCount ?? 0)}
          disabled
          fullWidth
          data-testid="device-detail-faceCount"
        />
        <LabelInput
          label={translate('device:detail.fields.markCount')}
          value={String(device.markCount ?? 0)}
          disabled
          fullWidth
          data-testid="device-detail-markCount"
        />
      </SidePanelFormLayout.Fields>
    </SidePanelFormLayout>
  );
};

export default DeviceDetailPanel;
